import { useEffect, useState } from "react";
import {
  Input,
  Select,
  Form,
  Space,
  Row,
  Col,
  Button,
  DatePicker,
  notification,
  Descriptions,
  message
} from "antd";
import {
  downloadReport,
  useFullReportMutation,
  useRefreshReportDownloadMutation
} from "../../redux/services/reportApi";
import { useGetLocationsQuery } from "../../redux/services/locationsApi";
import { useGetBrandsQuery } from "../../redux/services/brandApi";
import { useLazyGetBrandModelsQuery } from "../../redux/services/brandModelApi";
import { useAppSelector } from "../../redux/hooks";
import {
  reportTypeOptions,
  clientReportOptions,
  financeReportOptions,
  adminReportTypeOptions
} from "../../constants/helperConstants";
import { ReloadOutlined, MailOutlined } from "@ant-design/icons";
import ReportDownloadModal from "./ReportDownloadModal";

const { Option } = Select;
const { RangePicker } = DatePicker;

const fullReportTypes = {
  database: "Database",
  complaint_database: "Complaint Database",
  installations: "IR Master",
  invoicing_installation: "Invoicing Installation",
  invoicing_survey: "Invoicing Survey",
  payment_tracker: "Payment Tracker"
};

const ReportDownloadForm = (props: any) => {
  const [form] = Form.useForm();
  const auth = useAppSelector<any>((store) => store.auth);
  const user = auth.user;
  const [selectedRange, setSelectedRange] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [fullReportloading, setFullReportLoading] = useState(false);
  const [fullReportRefreshloading, setFullReportRefreshLoading] = useState(false);
  const [fullReportType, setFullReportType] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [brandId, setBrandId] = useState<string | null>(null);

  const [notifier, contextHolder] = notification.useNotification();

  const isEditUser = user?.roles?.some((role: { role_type: string }) =>
    [
      "super_user",
      "mis",
      "city_manager",
      "zonal_manager",
      "management",
      "finance_controller"
    ].includes(role.role_type)
  );

  const isAdminUser = user?.roles?.some((role: { role_type: string }) =>
    ["super_user", "mis"].includes(role.role_type)
  );

  const isSuperUser = user?.roles?.some((role: { role_type: string }) =>
    ["super_user"].includes(role.role_type)
  );

  const isFinanceController = user?.roles?.some((role: { role_type: string }) =>
    ["finance_controller"].includes(role.role_type)
  );

  const { data: brandsData } = useGetBrandsQuery({ all_pages: true });
  const brands = brandsData?.brands || [];

  const [triggerGetBrandModels, { data: brandModelsData }] = useLazyGetBrandModelsQuery();
  const brandModels = brandModelsData?.brand_models || [];

  const { data: zoneLocationsData } = useGetLocationsQuery({
    location_type: "zone",
    all_pages: true
  });
  const zones = zoneLocationsData?.locations;

  const filteredReportTypeOptions = isAdminUser
    ? [...reportTypeOptions, ...financeReportOptions, ...adminReportTypeOptions]
    : reportTypeOptions;
  const isClient = user?.user_type === "client";

  const clientBrand = brands.filter((brand: any) => brand?.id === user?.brand_id);

  const [fullReport] = useFullReportMutation();
  const [refreshReportDownload] = useRefreshReportDownloadMutation();

  const [brandModelCodeOptions, setBrandModelCodeOptions] = useState([]);

  const handleBrandSelect = (selectedBrandId: string | null) => {
    form.setFieldValue("brand_model_id", undefined);
    setBrandId(selectedBrandId);
  };

  useEffect(() => {
    if (brandId?.length) {
      triggerGetBrandModels({ all_pages: true, brand_id: brandId });
    } else {
      setBrandModelCodeOptions([]);
    }
  }, [brandId]);

  useEffect(() => {
    if (brandModels.length) {
      setBrandModelCodeOptions(
        brandModels.flat().map((model: any) => ({ value: model.id, label: model.code }))
      );
    }
  }, [brandModels]);

  const getFullReport = async (values: any) => {
    try {
      setFullReportLoading(true);
      setFullReportType(values.report_type);
      const response = await fullReport({ ...values }).unwrap();
      const { download_link } = response;
      if (download_link) {
        const anchorElement = window.document.createElement("a");
        anchorElement.href = download_link;
        document.body.appendChild(anchorElement);
        anchorElement.click();
      }
      setFullReportLoading(false);
    } catch (error: any) {
      setFullReportLoading(false);
    }
  };

  const refreshFullReportDownload = async (values: any) => {
    try {
      setFullReportRefreshLoading(true);
      setFullReportType(values.report_type);
      const response = await refreshReportDownload({ ...values }).unwrap();
      if (response?.success) {
        message.success(
          `Refresh full ${
            fullReportTypes[values.report_type as keyof typeof fullReportTypes]
          } report request submitted`
        );
      } else {
        message.error("Something went wrong !");
      }
      setFullReportRefreshLoading(false);
    } catch (error: any) {
      setFullReportRefreshLoading(false);
    }
  };

  const onFinish = async (values: any) => {
    setLoading(true);
    // code for on finish
    if (values.date_range && values.date_range[0]) {
      values["start_date"] = values.date_range[0]?.format("YYYY-MM-DD");
    }
    if (values.date_range && values.date_range[1]) {
      values["end_date"] = values.date_range[1]?.format("YYYY-MM-DD");
    } else {
      values["end_date"] = values.date_range[0]?.format("YYYY-MM-DD");
    }
    try {
      await Promise.resolve(downloadReport(values, auth, isClient));
    } catch (error: any) {
      notifier.error({
        message: error?.response?.data?.errors || "Unknown error occurred. Please try again.",
        placement: "top"
      });
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    // code for finish failed
    console.log(errorInfo);
  };

  // Function to handle RangePicker change
  const handleRangeChange = (dates: any, dateStrings: string[]) => {
    setSelectedRange(dates);
  };

  function disabledDate(current: any) {
    if (selectedRange && selectedRange.length === 0) {
      return false; // No range selected yet, all dates are enabled
    }

    if (selectedRange && selectedRange[0]) {
      const daysAfterStart = selectedRange[0].clone().add(31, "days");
      if (selectedRange[1] && selectedRange[1] > daysAfterStart) {
        const range = selectedRange;
        range[1] = null;
        setSelectedRange(range);
      }
      return current.isAfter(daysAfterStart);
    }

    return false;
  }

  const RefreshButton = (btnProps: any) => {
    return (
      <Button
        loading={fullReportRefreshloading && fullReportType === btnProps.reportType}
        icon={<ReloadOutlined />}
        style={{ float: "right", marginRight: "10px" }}
        onClick={() => refreshFullReportDownload({ report_type: btnProps.reportType })}
      >
        {fullReportRefreshloading && fullReportType === btnProps.reportType
          ? "Refreshing, please wait"
          : "Refresh"}
      </Button>
    );
  };

  return (
    <>
      {contextHolder}
      {(isAdminUser || isFinanceController || isClient) && (
        <Form
          name="report_download_form"
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          initialValues={{ date_range: [null, null] }}
          autoComplete="off"
        >
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col className="gutter-row" span={12}>
              <Form.Item
                label=""
                name="date_range"
                rules={[{ required: true, message: "Please select from date" }]}
              >
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    disabled
                    value={"Date Range"}
                    style={{ width: "200px", color: "rgba(0, 0, 0, 0.65)", cursor: "auto" }}
                  />
                  <Form.Item name="date_range" noStyle>
                    <RangePicker
                      disabledDate={disabledDate}
                      onCalendarChange={handleRangeChange}
                      format="DD-MM-YYYY"
                      value={[selectedRange && selectedRange[0], selectedRange && selectedRange[1]]}
                      disabled={[false, !selectedRange || (!selectedRange && !selectedRange[0])]}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            </Col>
            {(isClient || isEditUser) && (
              <Col className="gutter-row" span={12}>
                <Form.Item>
                  <Space.Compact style={{ width: "100%" }}>
                    <Input
                      disabled
                      value={"Brand"}
                      style={{ width: "200px", color: "rgba(0, 0, 0, 0.65)", cursor: "auto" }}
                    />
                    <Form.Item name="brand_id" noStyle>
                      <Select
                        placeholder="Select brand"
                        allowClear
                        showSearch
                        mode="multiple"
                        optionFilterProp="children"
                        onChange={handleBrandSelect}
                        filterOption={(input, option: any) =>
                          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {isClient
                          ? clientBrand?.map((brand: any) => (
                              <Option key={brand?.name} value={brand?.id}>
                                {brand?.name}
                              </Option>
                            ))
                          : brands?.map((brand: any) => (
                              <Option key={brand?.name} value={brand?.id}>
                                {brand?.name}
                              </Option>
                            ))}
                      </Select>
                    </Form.Item>
                  </Space.Compact>
                </Form.Item>
              </Col>
            )}
            <Col className="gutter-row" span={12}>
              <Form.Item>
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    disabled
                    value={"Brand Model Code"}
                    style={{
                      width: "200px",
                      color: "rgba(0, 0, 0, 0.65)",
                      cursor: "auto"
                    }}
                  />
                  <Form.Item name="brand_model_id" noStyle>
                    <Select
                      placeholder="Select Brand Model Code"
                      allowClear
                      showSearch
                      mode={"multiple"}
                      optionFilterProp="children"
                      filterOption={(inputValue, option: any) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      options={brandModelCodeOptions}
                    ></Select>
                  </Form.Item>
                </Space.Compact>
              </Form.Item>{" "}
              <Form.Item>
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    disabled
                    value={"Zone"}
                    style={{ width: "200px", color: "rgba(0, 0, 0, 0.65)", cursor: "auto" }}
                  />
                  <Form.Item name="zone_id" noStyle>
                    <Select
                      mode="multiple"
                      placeholder="Select zone"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option: any) =>
                        option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {zones?.map((zone: any) => (
                        <Option key={zone?.id} value={zone?.id}>
                          {zone?.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={12}>
              <Form.Item
                label=""
                name="report_type"
                rules={[{ required: true, message: "Please select report" }]}
              >
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    disabled
                    value={"Report"}
                    style={{ width: "200px", color: "rgba(0, 0, 0, 0.65)", cursor: "auto" }}
                  />
                  <Form.Item name="report_type" noStyle>
                    <Select
                      placeholder="Select Report"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(inputValue, option: any) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      options={
                        isClient
                          ? clientReportOptions
                          : isFinanceController
                          ? financeReportOptions
                          : filteredReportTypeOptions
                      }
                    ></Select>
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={12}>
              <Form.Item label="" name="page_number">
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    disabled
                    value={"Page Number"}
                    style={{ width: "200px", color: "rgba(0, 0, 0, 0.65)", cursor: "auto" }}
                  />
                  <Form.Item name="page_number" noStyle>
                    <Input min={1} placeholder="Page Number" type="number" />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            </Col>
            <Row style={{ display: "flex", flexDirection: "row" }}>
              <Col className="gutter-row" span={12}>
                <div style={{ marginBottom: 20, gap: 6, display: "flex" }}>
                  <Button
                    loading={loading}
                    form="report_download_form"
                    key="submit"
                    htmlType="submit"
                    type="primary"
                  >
                    {loading ? "Downloading, please wait" : "Download"}
                  </Button>
                </div>
              </Col>
            </Row>
          </Row>
        </Form>
      )}

      {isEditUser && (
        <Row style={{ display: "flex", flexDirection: "row" }}>
          <Col className="gutter-row" span={12}>
            <div style={{ marginBottom: 20, gap: 6, display: "flex" }}>
              <Button
                icon={<MailOutlined />}
                type="primary"
                onClick={() => {
                  setShowModal(true);
                }}
                style={{ marginLeft: "0px", marginTop: "20px" }}
              >
                {"Email Report Request"}
              </Button>
              <ReportDownloadModal showModal={showModal} setShowModal={setShowModal} />
            </div>
          </Col>
        </Row>
      )}

      {isSuperUser && (
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col className="gutter-row" span={24}>
            <Descriptions
              title=""
              bordered
              column={1}
              labelStyle={{ fontWeight: 700, width: 300 }}
              size="middle"
            >
              {Object.entries(fullReportTypes).map((report) => (
                <>
                  <Descriptions.Item key={report[0]} label={`Full ${report[1]} Report`}>
                    <Button
                      loading={fullReportloading && fullReportType === report[0]}
                      type="primary"
                      style={{ float: "right" }}
                      onClick={() => getFullReport({ report_type: report[0] })}
                    >
                      {fullReportloading && fullReportType === report[0]
                        ? "Downloading, please wait"
                        : "Download"}
                    </Button>
                    <RefreshButton reportType={report[0]} />
                  </Descriptions.Item>
                </>
              ))}
            </Descriptions>
          </Col>
        </Row>
      )}
    </>
  );
};

export default ReportDownloadForm;
